<script setup lang="ts">
// Mounted once in App.vue. Renders the active menu (if any), handles
// positioning and viewport-flip, click-outside, Escape, scroll, and blur
// dismissal, and keyboard navigation.

import { computed, nextTick, ref, watch } from 'vue'

import { closeMenu, useContextMenu, type MenuItem } from '../menus'

const { active } = useContextMenu()

const menuRef  = ref<HTMLElement | null>(null)
const position = ref<{ x: number; y: number }>({ x: 0, y: 0 })
const activeIdx = ref(-1)

const items = computed<MenuItem[]>(() => active.value?.items ?? [])

function actionableIndices(): number[] {
  return items.value
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => !item.separator && !item.disabled)
    .map(({ i }) => i)
}

function positionMenu() {
  const m = menuRef.value
  const a = active.value
  if (!m || !a) return

  const rect = m.getBoundingClientRect()
  let x: number
  let y: number

  if (a.anchor.kind === 'point') {
    x = a.anchor.x
    y = a.anchor.y
  } else {
    const arect = a.anchor.el.getBoundingClientRect()
    x = arect.left
    y = arect.bottom + 4
  }

  // Horizontal overflow: for element anchors, right-align; for point anchors,
  // shift left.
  if (x + rect.width > window.innerWidth) {
    if (a.anchor.kind === 'element') {
      x = a.anchor.el.getBoundingClientRect().right - rect.width
    } else {
      x = x - rect.width
    }
  }

  // Vertical overflow: for element anchors, flip above; for point anchors,
  // shift up.
  if (y + rect.height > window.innerHeight) {
    if (a.anchor.kind === 'element') {
      y = a.anchor.el.getBoundingClientRect().top - rect.height - 4
    } else {
      y = y - rect.height
    }
  }

  position.value = { x: Math.max(0, x), y: Math.max(0, y) }
}

function onClickOutside(e: Event) {
  const a = active.value
  if (!a) return
  const target = e.target as Node
  const m = menuRef.value
  if (m && m.contains(target)) return
  // A press on the element that anchors this menu is left to the anchor's
  // own click handler (which typically calls openMenu again — the framework
  // toggles when the anchor matches). Closing here would race the toggle
  // and leave the menu open after the click, breaking natural toggle UX.
  if (a.anchor.kind === 'element' && a.anchor.el.contains(target)) return
  closeMenu()
}

function onScrollCapture() {
  if (active.value) closeMenu()
}

function onKeydown(e: KeyboardEvent) {
  if (!active.value) return

  if (e.key === 'Escape') { e.preventDefault(); closeMenu(); return }

  const actionable = actionableIndices()

  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    if (actionable.length === 0) return
    const currentPos = actionable.indexOf(activeIdx.value)
    const dir = e.key === 'ArrowDown' ? 1 : -1
    const nextPos = currentPos === -1
      ? (dir === 1 ? 0 : actionable.length - 1)
      : (currentPos + dir + actionable.length) % actionable.length
    activeIdx.value = actionable[nextPos]!
  } else if (e.key === 'Enter' || e.key === ' ') {
    if (activeIdx.value === -1) return
    e.preventDefault()
    const item = items.value[activeIdx.value]
    if (item) void invoke(item)
  }
}

async function invoke(item: MenuItem) {
  if (item.disabled || item.separator) return
  const run = item.action
  closeMenu()
  if (run) {
    try { await run() } catch { /* swallow — caller can toast.error */ }
  }
}

// Wire and unwire document-level listeners based on menu presence, and
// re-position each time the menu is (re)opened.
//
// pointerdown (not mousedown) in capture=true. The engine's <cad-sketch>
// canvas calls preventDefault on its pointer events, which suppresses the
// compatibility mouse events entirely — a mousedown listener never fires
// on canvas clicks. pointerdown fires first regardless, and capture on
// document runs before the engine's own handler. anchor-and-menu-target
// exclusion inside onClickOutside keeps the toggle path clean.
//
// keydown also capture=true so a leaf element that swallows Escape via
// stopPropagation cannot disable menu dismissal.
watch(active, async (m) => {
  if (m) {
    activeIdx.value = -1
    await nextTick()
    positionMenu()
    document.addEventListener('pointerdown', onClickOutside, true)
    document.addEventListener('scroll',      onScrollCapture, true)
    window.addEventListener('resize', positionMenu)
    window.addEventListener('blur',   closeMenu)
    document.addEventListener('keydown', onKeydown, true)
  } else {
    document.removeEventListener('pointerdown', onClickOutside, true)
    document.removeEventListener('scroll',      onScrollCapture, true)
    window.removeEventListener('resize', positionMenu)
    window.removeEventListener('blur',   closeMenu)
    document.removeEventListener('keydown', onKeydown, true)
  }
})
</script>

<template>
  <Teleport to="body">
    <ul
      v-if="active"
      ref="menuRef"
      class="menu"
      role="menu"
      :style="{ left: `${position.x}px`, top: `${position.y}px` }"
    >
      <template v-for="(item, i) in items" :key="i">
        <li v-if="item.separator" class="menu-separator" role="separator" />
        <li v-else role="none">
          <button
            type="button"
            role="menuitem"
            class="menu-item"
            :class="{ 'menu-item--danger': item.kind === 'danger' }"
            :disabled="item.disabled"
            :data-active="i === activeIdx ? 'true' : undefined"
            @click="invoke(item)"
            @mouseenter="activeIdx = i"
          >
            <span v-if="item.icon" aria-hidden="true">{{ item.icon }}</span>
            <span>{{ item.label }}</span>
          </button>
        </li>
      </template>
      <li v-if="active.footer" class="menu-footer" aria-hidden="true">
        {{ active.footer }}
      </li>
    </ul>
  </Teleport>
</template>
